/**
 * @fileoverview The plant's public switch, on the card, and the numbers that go with it.
 *
 * One switch, one word. On, and every page behind this plant's share links
 * shows the gardener's name and the photographs they shared. Off, and those
 * pages show the plant and the invitation. Turning it on the first time asks
 * once, because that is the consent: the settings that apply to every page
 * live on the profile, and this is only the decision for this plant. Whether
 * to ask is the server's (needsConsent, needsGalleryName on the sharing read;
 * usePublicSwitch), so every phone asks the same.
 *
 * A plant's first time asks with the gallery's dialog (GalleryConsentDialog):
 * a public plant's shared photos are also offered to the website gallery,
 * where an approver looks at them, under the name chosen there. It asks again,
 * once, of a gardener who agreed before the gallery joined the switch and so
 * has no gallery name yet (needsGalleryName). A bed's switch keeps the plain question.
 * The bed question's foot is ActionRow (B111): Cancel left, the action right waiting as Button loading; the question stays open until the save settles.
 *
 * How many times the plant's links were opened is shown beside Share on the
 * card, where sharing happens, not here.
 *
 * It has three looks (the bed card's "switch" look went with its header row, B158 job 6): the "tool" (a boxed icon over its word), the "compact" (the standard
 * shadcn Switch, ui/switch, with a small "Public" label beside it) and the "tile" (B136: the plant card's
 * Public tile, a TileButton that is the switch, a small track and the word, one third of the first row beside the
 * filled Share tile; nothing is drawn while the answer is on its way).
 * Given disabledReason (a plant with no photo yet), the compact switch is
 * drawn at once, greyed and off, its reason its tooltip and, on a tap, a
 * toast, as Share's greyed button does: it is there, and a photo is what
 * turns it on (owner, 2026-10-15: "the public switch should
 * be there and disabled").
 *
 * Only the garden's owner turns it (B37; owner, 2026-10-20): for a member
 * tending someone else's garden it shows the owner's choice, greyed, and a tap
 * says "Only the garden's owner can change this." (useOwnerOnly) in a toast;
 * the server refuses a member's change 403 OWNER_ONLY.
 *
 * On the Plants page the card gives the switch what the plants brief says (briefSharing, B110): the switch then reads
 * nothing of its own, and seeds its cache from the brief so a flip shows at once. A single bed or plant screen gives
 * nothing and keeps its own read.
 *
 * Where: the plant card (CardTiles, the "tile" look) and the bed card
 * (LocationCard, the "tile" look, for a bed's subject).
 * API: GET /shares/plant/:id or GET /shares/location/:id (getSubjectSharing);
 * PUT /shares/plant/:id { isPublic, galleryName } or PUT
 * /shares/location/:id { isPublic } (setSubjectPublic); GET /gallery/mine
 * (useMyGallery).
 * Settings: SHARING_STALE_MS (config/tunables/garden).
 * Words (en/es): garden.ownerOnly; plants.card.public.* (label, short, on, off, needsPhoto, galleryTitle,
 * galleryConfirm, consentTitle, consentAction), publicConsent.* (the three
 * bullets, PublicConsentBullets),
 * sitewide.cancel; the gallery dialog's gallery.share.* (gallery.share.failed
 * is the toast when a change could not be saved).
 * Watch out: the switch draws nothing until the server answers; a failed change leaves the switch as it was
 * and says so in a toast: for a failure on our side (a 500, no connection;
 * D31, lib/serverFailure) the standard sentence, reported to Sentry, in
 * place of gallery.share.failed (lib/errorMessage).
 *
 * @module PublicPageSwitch
 */

import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { useAuth } from '@/shared/hooks/useAuth';
import { useToast } from '@/shared/components/ui/use-toast';
import { Users } from 'lucide-react';
import { cn } from '@/lib/utils';
import { errorMessage } from '@/shared/lib/errorMessage';
import { ActionRow } from '@/shared/components/ui/ActionRow';
import { Button } from '@/shared/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { getSubjectSharing, setSubjectPublic, type PlantSharing } from '@/shared/api/shares';

import { plantSubject, type Subject } from '@/shared/api/subject';
import { Switch } from '@/shared/components/ui/switch';
import { TileButton } from '@/shared/components/ui/TileButton';
import type { Plant } from '@/shared/types/plant';
import { LOCATION_SHARING_QUERY_KEY, PLANT_SHARING_QUERY_KEY, SHARING_STALE_MS } from '@/config/tunables/garden';
import { LOCATIONS_QUERY_KEY, PLANTS_QUERY_KEY } from '@/config/tunables/ui';
import { refreshPlantsBrief } from '@/shared/hooks/usePlantsBrief';
import { PublicConsentBullets } from './PublicConsentBullets';
import { GalleryConsentDialog } from '@/features/gallery/components/GalleryConsentDialog';
import { MY_GALLERY_KEY, useMyGallery } from '@/features/gallery/lib/useMyGallery';
import { usePublicSwitch } from './usePublicSwitch';
import { useOwnerOnly } from '@/shared/hooks/useOwnerOnly';

/**
 * One size for the switch's tool look: its icon with its name under it, as
 * the card's photo row had it (the row went with the dots, 2026-10-14).
 */
const TOOL_CLASS =
  'flex h-12 min-w-12 shrink-0 flex-col items-center justify-center gap-0.5 rounded-sm border bg-background px-1.5 shadow-sm transition-colors hover:bg-muted';
/** The tool's name under its icon, in rem so it grows with the gardener's text size. */
const TOOL_LABEL = 'text-[0.6875rem] font-medium leading-none';

/**
 * The little switch track on the Public tile: the active colour with the knob at the right when on, the secondary colour with
 * the knob at the left when off. It is a picture for the tile's button, which is the switch.
 *
 * @param props.on - Whether Public is on.
 */
const SwitchTrack: React.FC<{ on: boolean }> = ({ on }) => (
  <span className={cn('relative inline-block h-5 w-[2.125rem] shrink-0 rounded-full transition-colors', on ? 'bg-active' : 'bg-secondary')} aria-hidden="true">
    <span className={cn('absolute top-0.5 h-4 w-4 rounded-full bg-card shadow transition-all', on ? 'left-4' : 'left-0.5')} />
  </span>
);

/** Whose switch it is, and how it is drawn. */
interface PublicPageSwitchProps {
  /** The plant's switch; or give a subject, for a location's. */
  plantObjId?: number;
  /** The plant or location the switch is for; wins over plantObjId. */
  subject?: Subject;
  /**
   * How it looks: a tool beside the others, or the compact look: the standard
   * Switch with a small "Public" label, or the tile: the plant card's Public tile, a TileButton that is the switch.
   */
  look?: 'tool' | 'compact' | 'tile';
  /**
   * The plant's sharing from the plants brief (B110). A value (or null while the brief is on its way) means the switch makes
   * no read of its own; left out (undefined: a bed's, a single plant's, or a brief that could not read it), it reads GET /shares/... itself.
   */
  briefSharing?: PlantSharing | null;
  /** Why the switch cannot be used yet (the compact and tile looks): drawn greyed and off at once, this its tooltip and a tap's toast. */
  disabledReason?: string;
}

/**
 * The Public tool on a plant or location card: on puts it on the gardener's
 * garden page with their name. The first time, it asks.
 *
 * @param props - PublicPageSwitchProps.
 */
export const PublicPageSwitch: React.FC<PublicPageSwitchProps> = ({ plantObjId, subject: subjectProp, look = 'tool', disabledReason, briefSharing }) => {
  const { reactTranslate: t } = useTranslation();
  const { mysqlUser } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  /** The plant or bed the switch is for; one subject object while the props stay the same. */
  const subject = React.useMemo(() => subjectProp ?? plantSubject(plantObjId ?? 0), [subjectProp, plantObjId]);
  const key = subject.kind === 'plant' ? [PLANT_SHARING_QUERY_KEY, subject.id] : [LOCATION_SHARING_QUERY_KEY, subject.id];
  /** The brief answers for this switch: it makes no read of its own. */
  const fromBrief = briefSharing !== undefined;
  const { data: cached, error: readError } = useQuery({ queryKey: key, queryFn: () => getSubjectSharing(subject), staleTime: SHARING_STALE_MS, enabled: !fromBrief });
  /** The switch's state: the brief's for the Plants page (kept in the cache, which a flip updates at once), else the read's. */
  const data = fromBrief ? cached ?? briefSharing ?? undefined : cached;
  // Each time the brief says something new, the cache says it too, so the next flip builds on it.
  React.useEffect(() => {
    if (briefSharing) queryClient.setQueryData(key, briefSharing);
    // key changes only with the subject; briefSharing's fields are what matter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient, subject.kind, subject.id, briefSharing?.isPublic, briefSharing?.shares, briefSharing?.visits, briefSharing?.needsConsent, briefSharing?.needsGalleryName]);
  const { data: gallery } = useMyGallery();
  /** Ties the compact look's label to its switch. */
  const switchId = React.useId();
  const isPlant = subject.kind === 'plant';
  /** In someone else's garden: why the switch is locked (B37); null in the person's own. */
  const { lockedReason } = useOwnerOnly();

  /**
   * The first question, as the server says it (usePublicSwitch); apply saves through toggle, declared below.
   */
  const sw = usePublicSwitch({
    sharing: data,
    readError,
    where: 'PublicPageSwitch',
    apply: (on, galleryName, onSettled) => toggle.mutate({ on, galleryName }, onSettled ? { onSettled } : undefined),
  });

  /**
   * Turns the page on or off (with the gallery name, the first time), and
   * keeps the garden's copy of is_public in step. A change that could not be
   * saved leaves the switch as it was and says so.
   */
  const toggle = useMutation({
    mutationFn: ({ on, galleryName }: { on: boolean; galleryName?: string }) => setSubjectPublic(subject, on, galleryName),
    onSuccess: (_, { on, galleryName }) => {
      sw.saved(on);
      // The name is saved now, and the plant's photos are in (or out of) the gallery.
      if (galleryName || isPlant) void queryClient.invalidateQueries({ queryKey: MY_GALLERY_KEY });
      queryClient.setQueryData(key, (current: typeof data) => (current ? { ...current, isPublic: on, needsConsent: current.needsConsent && !on } : current));
      // The server says what the next first question is (a held gallery name is asked again): on the Plants page in the next brief, elsewhere in this switch's own read.
      void (fromBrief ? refreshPlantsBrief(queryClient) : queryClient.invalidateQueries({ queryKey: key }));
      // The garden carries is_public too, for the Public filter: kept in step here.
      if (subject.kind === 'plant') {
        queryClient.setQueriesData<Plant[]>({ queryKey: [PLANTS_QUERY_KEY] }, (garden) => (Array.isArray(garden)
          ? garden.map((plant) => (plant.plant_obj_id === subject.id ? { ...plant, is_public: on ? 1 : 0 } : plant))
          : garden));
      } else {
        void queryClient.invalidateQueries({ queryKey: [LOCATIONS_QUERY_KEY] });
      }
    },
    onError: (error) => {
      toast({ title: errorMessage(error, t('gallery.share.failed')), variant: 'destructive' });
    },
  });

  /**
   * A press of the switch: for a member, only the reason it is locked (a toast); otherwise the first question or the change.
   *
   * @param on - The state pressed for.
   */
  const press = (on: boolean): void => {
    if (lockedReason) {
      toast({ description: lockedReason });
      return;
    }
    sw.press(on);
  };
  /** The tooltip: why it is locked, or what it is and whether it is on. */
  const titleOf = (isPublic: boolean): string => lockedReason
    ?? `${t('plants.card.public.label')}: ${isPublic ? t('plants.card.public.on') : t('plants.card.public.off')}`;

  // The plant card's tile with no photo yet: dim, off, and a tap says why.
  if (look === 'tile' && disabledReason) {
    return (
      <TileButton
        size="big"
        role="switch"
        aria-checked={false}
        aria-label={t('plants.card.public.label')}
        title={disabledReason}
        dim
        icon={<SwitchTrack on={false} />}
        onClick={() => toast({ description: disabledReason })}
      >
        {t('plants.card.public.short')}
      </TileButton>
    );
  }

  // Not usable yet (a plant with no photo): there, greyed and off, without waiting for the server; a tap says why, as Share's does.
  if (look === 'compact' && disabledReason) {
    return (
      <div className="flex min-h-8 items-center gap-2" title={disabledReason}>
        <Switch
          id={switchId}
          checked={false}
          aria-disabled
          aria-label={t('plants.card.public.label')}
          aria-describedby={`${switchId}-why`}
          className="opacity-50"
          onCheckedChange={() => toast({ description: disabledReason })}
        />
        <label htmlFor={switchId} className="cursor-pointer text-sm font-medium text-muted-foreground">{t('plants.card.public.short')}</label>
        <span id={`${switchId}-why`} className="sr-only">{disabledReason}</span>
      </div>
    );
  }

  if (!data) {
    // Nothing is drawn until the answer comes (it never says Public is on or off); the card's row keeps Share at its right end meanwhile.
    return null;
  }

  return (
    // Sized and boxed like the buttons beside it. The sentence that used to
    // sit under it lives in the tooltip; the consent dialog carries the rest.
    <>
    {look === 'tile' ? (
      <TileButton
        size="big"
        role="switch"
        aria-checked={data.isPublic}
        aria-label={t('plants.card.public.label')}
        title={titleOf(data.isPublic)}
        disabled={toggle.isPending}
        dim={Boolean(lockedReason)}
        icon={<SwitchTrack on={data.isPublic} />}
        onClick={() => press(!data.isPublic)}
      >
        {t('plants.card.public.short')}
      </TileButton>
    ) : look === 'compact' ? (
      // The standard Switch with a small label: a tap on either turns it.
      <div className="flex min-h-8 items-center gap-2">
        <Switch
          id={switchId}
          checked={data.isPublic}
          aria-label={t('plants.card.public.label')}
          title={titleOf(data.isPublic)}
          disabled={toggle.isPending}
          aria-disabled={lockedReason ? true : undefined}
          className={cn(lockedReason && 'opacity-50')}
          onCheckedChange={press}
        />
        <label htmlFor={switchId} className="cursor-pointer text-sm font-medium text-foreground">{t('plants.card.public.short')}</label>
      </div>
    ) : (
    <button
      type="button"
      role="switch"
      aria-checked={data.isPublic}
      aria-label={t('plants.card.public.label')}
      title={titleOf(data.isPublic)}
      disabled={toggle.isPending}
      aria-disabled={lockedReason ? true : undefined}
      onClick={() => press(!data.isPublic)}
      className={cn(TOOL_CLASS, 'relative', data.isPublic && 'border-active bg-active text-active-foreground hover:bg-active/90', lockedReason && 'opacity-50')}
    >
      <Users className="h-5 w-5" aria-hidden="true" />
      <span className={TOOL_LABEL}>{t('plants.card.public.short')}</span>
    </button>
    )}

      {/* A plant: asked once, with the gallery's notice and the name to show. */}
      {isPlant && (
        <GalleryConsentDialog
          open={sw.asking}
          count={1}
          initialName={gallery?.name ?? mysqlUser?.display_name ?? ''}
          busy={toggle.isPending}
          onCancel={() => sw.setAsking(false)}
          onConfirm={(galleryName) => sw.confirm(galleryName)}
          title={t('plants.card.public.galleryTitle')}
          notice={<PublicConsentBullets kind="plant" />}
          confirmLabel={t('plants.card.public.galleryConfirm')}
        />
      )}

      {/* A bed: asked once, the first time anything of theirs goes public. */}
      <Dialog open={sw.asking && !isPlant} onOpenChange={sw.setAsking}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('plants.card.public.consentTitle')}</DialogTitle>
            <PublicConsentBullets kind="bed" />
          </DialogHeader>
          <DialogFooter>
            <ActionRow onCancel={() => sw.setAsking(false)} disabled={toggle.isPending}>
              <Button loading={toggle.isPending} onClick={() => sw.confirm()}>{t('plants.card.public.consentAction')}</Button>
            </ActionRow>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
